import { RoleEnum } from "@/types/auth/roles";
import { User } from "@/types/auth/index";
import { hasTraderFullAccess } from "./traderAccess";
import { hasExceptionalSupportShiftAccess } from "./supportShiftAccess";
import { hasRevokedAccess } from "./revokedAccess";
import { hasExceptionalRefundAccess } from "./refundAccess";
import { hasExceptionalPenaltyAccess } from "./penaltyAccess";
import { hasExceptionalAppealAccess } from "./appealSuccessAccess";
import { hasPermission, Permission } from './permissions';

export enum PageKey {
  DASHBOARD = "DASHBOARD",
  USERS = "USERS",
  MERCHANTS = "MERCHANTS",
  MERCHANT_BALANCES = "MERCHANT_BALANCES",
  TRADERS = "TRADERS",
  TRADER_OPERATIONS = "TRADER_OPERATIONS",
  TRADER_SUPPORT = "TRADER_SUPPORT",
  SETTINGS = "SETTINGS",
  FINANCE = "FINANCE",
  OFFERS = "OFFERS",
  PAYOUTS_OUT = "PAYOUTS_OUT",
  PAYOUTS_OUT_OPERATIONS = "PAYOUTS_OUT_OPERATIONS",
  TRANSACTIONS = "TRANSACTIONS",
  APPEALS = "APPEALS",
  REQUISITES = "REQUISITES",
  GEO_EMERGENCY = "GEO_EMERGENCY",
  SUPPORT_SHIFTS = "SUPPORT_SHIFTS",
  DEPOSITS = "DEPOSITS",
  SANDBOX_REQUISITES = "SANDBOX_REQUISITES",
  INVITE_TOKENS = "INVITE_TOKENS",
  SETTLEMENTS = "SETTLEMENTS",
  ANTIFRAUD = "ANTIFRAUD",
  VERIFICATION = "VERIFICATION",
  NEWS = "NEWS",
  DEVELOPMENT = "DEVELOPMENT",
  REFUND_REQUESTS = "REFUND_REQUESTS",
  PENALTIES = "PENALTIES",
  LOSSES = "LOSSES",
}

/**
 * Центральная матрица: какие роли имеют доступ к каким страницам.
 *
 * Примечания:
 * - `is_superuser === true` всегда обходит эту матрицу.
 * - `role === ADMIN` трактуется как «доступ ко всему» (сейчас заглушка, дублирующая is_superuser по продуктовому решению).
 * - Остальные роли кроме SUPPORT предусмотрены на будущее, доступ для них пока не настроен.
 */
export const PAGE_ACCESS_BY_ROLE: Record<PageKey, RoleEnum[]> = {
  [PageKey.DASHBOARD]: [RoleEnum.CLIENT_MANAGER],
  [PageKey.USERS]: [],
  [PageKey.TRADERS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [PageKey.TRADER_OPERATIONS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [PageKey.TRADER_SUPPORT]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [PageKey.TRANSACTIONS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER, RoleEnum.CLIENT_MANAGER],
  [PageKey.APPEALS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [PageKey.REQUISITES]: [RoleEnum.SUPPORT],
  [PageKey.PAYOUTS_OUT]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  /** Операционные вкладки OUT: PRODUCT_MANAGER, ADMIN и is_superuser. */
  [PageKey.PAYOUTS_OUT_OPERATIONS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],

  /** Мерчанты: PRODUCT_MANAGER — просмотр и управление ставками (вкл./выкл.). */
  [PageKey.MERCHANTS]: [RoleEnum.PRODUCT_MANAGER, RoleEnum.CLIENT_MANAGER],
  [PageKey.MERCHANT_BALANCES]: [RoleEnum.CLIENT_MANAGER],
  [PageKey.SETTINGS]: [],
  [PageKey.FINANCE]: [RoleEnum.CLIENT_MANAGER],
  [PageKey.OFFERS]: [RoleEnum.CLIENT_MANAGER],
  [PageKey.GEO_EMERGENCY]: [RoleEnum.ADMIN],
  [PageKey.SUPPORT_SHIFTS]: [RoleEnum.SUPPORT, RoleEnum.PRODUCT_MANAGER],
  [PageKey.DEPOSITS]: [RoleEnum.ADMIN],
  [PageKey.SANDBOX_REQUISITES]: [],
  [PageKey.INVITE_TOKENS]: [RoleEnum.CLIENT_MANAGER],
  [PageKey.SETTLEMENTS]: [],
  [PageKey.ANTIFRAUD]: [RoleEnum.PRODUCT_MANAGER],
  [PageKey.VERIFICATION]: [RoleEnum.ADMIN],
  [PageKey.NEWS]: [RoleEnum.ADMIN],
  [PageKey.DEVELOPMENT]: [RoleEnum.ADMIN],
  [PageKey.REFUND_REQUESTS]: [],
  [PageKey.PENALTIES]: [],
  [PageKey.LOSSES]: [],
};

const TRADER_FULL_ACCESS_PAGES = new Set<PageKey>([
  PageKey.TRADERS,
  PageKey.TRADER_OPERATIONS,
  PageKey.TRADER_SUPPORT,
  PageKey.TRANSACTIONS,
  PageKey.APPEALS,
  PageKey.REQUISITES,
  PageKey.PAYOUTS_OUT,
  PageKey.PAYOUTS_OUT_OPERATIONS,
  PageKey.DEPOSITS,
  PageKey.VERIFICATION,
  PageKey.NEWS,
  PageKey.INVITE_TOKENS,
]);

export function canAccessPage(
  user: User | null | undefined,
  page: PageKey
): boolean {
  if (!user) return false;
  if (hasRevokedAccess(user)) return false;
  if (user.is_superuser) return true;
  if (user.role === RoleEnum.ADMIN) return true;
  if (page === PageKey.DASHBOARD && hasPermission(user, Permission.DASHBOARD_READ)) return true;
  if (
    (page === PageKey.MERCHANTS || page === PageKey.MERCHANT_BALANCES)
    && (
      hasPermission(user, Permission.MERCHANTS_READ)
      || hasPermission(user, Permission.MERCHANTS_MANAGE)
    )
  ) return true;
  if (
    page === PageKey.OFFERS
    && (
      hasPermission(user, Permission.OFFERS_READ)
      || hasPermission(user, Permission.OFFERS_MANAGE)
    )
  ) return true;
  if (page === PageKey.TRADERS && hasPermission(user, Permission.TRADERS_READ)) return true;
  if (page === PageKey.TRANSACTIONS && hasPermission(user, Permission.TRANSACTIONS_READ)) return true;
  if (page === PageKey.REQUISITES && hasPermission(user, Permission.REQUISITES_MANAGE)) return true;
  if ((page === PageKey.PAYOUTS_OUT || page === PageKey.PAYOUTS_OUT_OPERATIONS)
    && hasPermission(user, Permission.PAYOUTS_SUPPORT)) return true;
  if (page === PageKey.REFUND_REQUESTS && hasPermission(user, Permission.REFUND_REQUESTS_READ)) return true;
  if (page === PageKey.REFUND_REQUESTS) return hasExceptionalRefundAccess(user);
  if (page === PageKey.PENALTIES) return hasExceptionalPenaltyAccess(user);
  if (
    page === PageKey.LOSSES
    && (
      hasPermission(user, Permission.LOSSES_READ)
      || hasPermission(user, Permission.LOSSES_MANAGE)
    )
  ) return true;
  if (page === PageKey.APPEALS && hasExceptionalAppealAccess(user)) return true;
  if (page === PageKey.SUPPORT_SHIFTS && hasExceptionalSupportShiftAccess(user)) return true;
  if (hasTraderFullAccess(user) && TRADER_FULL_ACCESS_PAGES.has(page)) return true;
  return PAGE_ACCESS_BY_ROLE[page].includes(user.role);
}

export function getDefaultPathForUser(user: User | null | undefined): string {
  if (!user) return "/login";
  if (hasRevokedAccess(user)) return "/login";
  if (user.is_superuser || user.role === RoleEnum.ADMIN) return "/dashboard";
  if (hasExceptionalPenaltyAccess(user)) return "/penalties";
  if (
    user.role === RoleEnum.SUPPORT ||
    user.role === RoleEnum.PRODUCT_MANAGER ||
    user.role === RoleEnum.CLIENT_MANAGER
  )
    return "/transactions";
  if (
    hasPermission(user, Permission.MERCHANTS_READ)
    || hasPermission(user, Permission.MERCHANTS_MANAGE)
  ) return '/merchants';
  if (
    hasPermission(user, Permission.OFFERS_READ)
    || hasPermission(user, Permission.OFFERS_MANAGE)
  ) return '/offers';
  if (hasPermission(user, Permission.DASHBOARD_READ)) return '/dashboard';
  if (hasExceptionalSupportShiftAccess(user)) return "/support-shifts";
  if (hasPermission(user, Permission.TRADERS_READ)) return '/traders';
  if (hasPermission(user, Permission.REFUND_REQUESTS_READ)) return '/refund-requests';
  if (
    hasPermission(user, Permission.LOSSES_READ)
    || hasPermission(user, Permission.LOSSES_MANAGE)
  ) return '/losses';
  // Для остальных ролей кроме SUPPORT доступ пока не настроен
  return "/login";
}
